import axios from "axios";
import { toast } from "sonner";
import { putCache, getCache, enqueue } from "@/lib/offlineDb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  // Send the httpOnly `f2_token` cookie with every request. Replaces
  // the old `Authorization: Bearer` header pulled from localStorage —
  // the cookie is unreadable to JavaScript, so an XSS payload can no
  // longer exfiltrate the JWT.
  withCredentials: true,
});

// URL key for cache lookup. We strip the host so a redeploy under a new
// preview URL still hits the same cached row.
function _cacheKey(config) {
  const url = (config.baseURL || "") + (config.url || "");
  return url.replace(API_BASE, "");
}

// We only cache JSON GETs that are not files/binary downloads, not closeout
// job polls, and not anything below `/files/...` (those return blobs).
function _isCacheableGet(config) {
  if (config.method !== "get") return false;
  const u = config.url || "";
  if (u.includes("/files/") && u.includes("/download")) return false;
  if (u.includes("/closeout/jobs/") && u.includes("/download")) return false;
  if (u.includes("/raw/download")) return false;
  if (config.responseType && config.responseType !== "json" && config.responseType !== "") return false;
  return true;
}

api.interceptors.response.use(
  async (r) => {
    // Write-through cache for cacheable JSON GETs.
    if (_isCacheableGet(r.config)) {
      try {
        await putCache(_cacheKey(r.config), r.status, r.data);
      } catch (e) {
        // Cache write failures are non-fatal, but log them so a broken
        // IndexedDB (e.g. private mode, quota exceeded) is debuggable.
        console.warn("[api] cache write failed", _cacheKey(r.config), e);
      }
    }
    return r;
  },
  async (err) => {
    const cfg = err?.config || {};
    const isNetworkErr = !err?.response;
    if (err?.response?.status === 401) {
      // The cookie is httpOnly; we can't clear it from JS. The server
      // is the only thing that can `delete_cookie` it — call /logout to
      // do that on our behalf, then drop our local user cache.
      try {
        await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
      } catch (e) {
        // Best-effort — even if logout 401s the cookie should expire on
        // its own. Don't block the redirect on it, but surface for debug.
        console.warn("[api] 401 logout cleanup failed", e);
      }
      localStorage.removeItem("f2_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    // Offline GET → serve cached copy if we have one. We tag the response
    // with `fromCache: true` so callers can show a stale-data indicator if
    // they want.
    if (isNetworkErr && _isCacheableGet(cfg)) {
      try {
        const hit = await getCache(_cacheKey(cfg));
        if (hit) {
          return {
            data: hit.data,
            status: hit.status || 200,
            statusText: "OK (offline cache)",
            headers: {},
            config: cfg,
            fromCache: true,
          };
        }
      } catch (e) {
        // IndexedDB read failed; we'll fall through and reject so the
        // caller can show a real "offline" error, but log the cause.
        console.warn("[api] offline cache read failed", _cacheKey(cfg), e);
      }
    }
    // Offline mutation → queue it (for JSON safety forms + generic patch).
    // Multipart uploads are queued by the upload helper directly because
    // axios FormData isn't easily reconstructable from the failed config.
    if (isNetworkErr && cfg.__offlineQueueable) {
      try {
        await enqueue(cfg.__offlineQueueable);
        window.dispatchEvent(new CustomEvent("f2:queue-changed"));
        return {
          data: { queued: true, ...(cfg.__offlineOptimistic || {}) },
          status: 202,
          statusText: "Queued offline",
          headers: {},
          config: cfg,
          queued: true,
        };
      } catch (e) {
        // Offline queue is broken (IndexedDB unavailable / quota). The
        // user is offline AND we can't even queue — they need to know.
        console.error("[api] offline enqueue failed", e);
        toast.error("Couldn't save offline — your changes may be lost. Please reconnect and retry.");
      }
    }
    return Promise.reject(err);
  }
);

export const fileUrl = (fileId, opts = {}) => {
  // Same-origin file download URL. The browser will attach the
  // `f2_token` httpOnly cookie automatically, so the URL itself stays
  // clean — no JWT in browser history, referer headers, or proxy logs.
  //
  // `opts.size` (optional): "thumb" | "medium" | "full" — the backend
  //   resizes the JPEG on the fly and caches the result in-process.
  //   Use `thumb` (~30 KB) for any grid/list view; before this knob,
  //   the Photos tab on big projects forced ~550 MB of camera-original
  //   bytes through the wire just to render 48px thumbnails.
  //
  // For cross-origin or shareable links, prefer `useSignedUrl` /
  // `signDownload()` from "@/lib/signedUrl" which mints a short-lived
  // HMAC-signed URL.
  const qs = opts.size ? `?size=${encodeURIComponent(opts.size)}` : "";
  return `${API_BASE}/files/${fileId}/download${qs}`;
};
